import { theme } from 'antd';
import type { ThemeConfig } from 'antd';

// DYEN Brand Colors
export const brandColors = {
  primary: '#FFC200',        // Brand yellow
  primaryHover: '#E6AF00',   // Darker yellow for hover
  primaryActive: '#CC9B00',  // Even darker for active/pressed states

  // Dark theme colors
  dark: {
    bg: '#0D0D0D',
    bgSecondary: '#1A1A1A',
    bgTertiary: '#252525',
    border: '#333333',
    text: '#FFFFFF',
    textSecondary: '#A0A0A0',
  },

  // Light theme colors
  light: {
    bg: '#F0F2F5',          // Soft gray for main background
    bgSecondary: '#FAFAFA', // Warm off-white for cards/sidebar
    bgTertiary: '#F5F5F5',  // Slightly darker off-white for inputs/hovers
    border: '#E0E0E0',
    text: '#1A1A1A',
    textSecondary: '#666666',
  },

  // Status colors
  success: '#52C41A',
  warning: '#FAAD14',
  error: '#FF4D4F',
  info: '#1890FF',
};

// Dark Theme Configuration
export const darkTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    colorPrimary: brandColors.primary,
    colorBgContainer: brandColors.dark.bgSecondary,
    colorBgElevated: brandColors.dark.bgTertiary,
    colorBgLayout: brandColors.dark.bg,
    colorText: brandColors.dark.text,
    colorTextSecondary: brandColors.dark.textSecondary,
    colorBorder: brandColors.dark.border,
    colorBorderSecondary: brandColors.dark.border,
    borderRadius: 8,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  components: {
    Layout: {
      siderBg: brandColors.dark.bgSecondary,
      headerBg: brandColors.dark.bgSecondary,
      bodyBg: brandColors.dark.bg,
      footerBg: brandColors.dark.bgSecondary,
    },
    Menu: {
      darkItemBg: 'transparent',
      darkSubMenuItemBg: 'transparent',
      darkItemColor: brandColors.dark.text,
      darkItemHoverColor: brandColors.primary,
      darkItemHoverBg: 'rgba(255, 194, 0, 0.1)',
      darkItemSelectedBg: 'rgba(255, 194, 0, 0.15)',
      darkItemSelectedColor: brandColors.primary,
      itemMarginBlock: 4,
      itemMarginInline: 8,
      itemPaddingInline: 16,
      itemBorderRadius: 8,
    },
    Button: {
      primaryColor: '#000000',
      colorPrimaryHover: brandColors.primaryHover,
      colorPrimaryActive: brandColors.primaryActive,
    },
    Card: {
      colorBgContainer: brandColors.dark.bgTertiary,
    },
    Input: {
      colorBgContainer: brandColors.dark.bgTertiary,
      colorBorder: brandColors.dark.border,
    },
    Select: {
      colorBgContainer: brandColors.dark.bgTertiary,
      colorBorder: brandColors.dark.border,
    },
    Table: {
      colorBgContainer: brandColors.dark.bgTertiary,
      headerBg: brandColors.dark.bgSecondary,
    },
    Modal: {
      contentBg: brandColors.dark.bgSecondary,
      headerBg: brandColors.dark.bgSecondary,
    },
    Dropdown: {
      colorBgElevated: brandColors.dark.bgSecondary,
    },
  },
};

// Light Theme Configuration
export const lightTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorPrimary: brandColors.primary,
    colorBgContainer: brandColors.light.bgSecondary,
    colorBgElevated: brandColors.light.bgSecondary,
    colorBgLayout: brandColors.light.bg,
    colorText: brandColors.light.text,
    colorTextSecondary: brandColors.light.textSecondary,
    colorBorder: brandColors.light.border,
    colorBorderSecondary: brandColors.light.border,
    borderRadius: 8,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  components: {
    Layout: {
      siderBg: brandColors.light.bgSecondary,
      headerBg: brandColors.light.bgSecondary,
      bodyBg: brandColors.light.bg,
      footerBg: brandColors.light.bgSecondary,
    },
    Menu: {
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemColor: brandColors.light.text,
      itemHoverColor: brandColors.light.text,
      itemHoverBg: 'rgba(255, 194, 0, 0.1)',
      itemSelectedBg: 'rgba(255, 194, 0, 0.15)',
      itemSelectedColor: brandColors.light.text,
      itemMarginBlock: 4,
      itemMarginInline: 8,
      itemPaddingInline: 16,
      itemBorderRadius: 8,
    },
    Button: {
      primaryColor: '#000000',
      colorPrimaryHover: brandColors.primaryHover,
      colorPrimaryActive: brandColors.primaryActive,
    },
    Card: {
      colorBgContainer: brandColors.light.bgSecondary,
    },
    Input: {
      colorBgContainer: brandColors.light.bgSecondary,
      colorBorder: brandColors.light.border,
    },
    Select: {
      colorBgContainer: brandColors.light.bgSecondary,
      colorBorder: brandColors.light.border,
    },
    Table: {
      colorBgContainer: brandColors.light.bgSecondary,
      headerBg: brandColors.light.bgTertiary,
    },
    Modal: {
      contentBg: brandColors.light.bgSecondary,
      headerBg: brandColors.light.bgSecondary,
    },
    Dropdown: {
      colorBgElevated: brandColors.light.bgSecondary,
    },
  },
};

export const getThemeConfig = (isDark: boolean): ThemeConfig => {
  return isDark ? darkTheme : lightTheme;
};
